/* ==========================================================================
   SAQR — theme.js
   Vanilla ES6 module. No dependencies.
   --------------------------------------------------------------------------
   Modules:
     - utils
     - scrollFadeIn
     - headerScroll
     - menuDrawer
     - searchDrawer
     - cartDrawer
     - quickAdd
     - productForm
     - productGallery
     - filters / sort
     - wishlist
     - announcementBar
   ========================================================================== */

const $  = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

const formatMoney = (cents) => {
  // Round to whole units to match the Liquid `format-money` snippet.
  const rounded = Math.round(cents / 100);
  const value = rounded.toLocaleString('en-US');
  // New Gulf currency symbols: render the webfont glyph instead of legacy text.
  if (window.theme?.currency === 'SAR') {
    return '<span class="sar-symbol" role="img" aria-label="SAR"></span>' + value;
  }
  if (window.theme?.currency === 'AED') {
    return '<span class="aed-symbol" role="img" aria-label="AED"></span>' + value;
  }
  const fmt = (window.theme?.moneyFormat || '${{amount}}');
  return fmt
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, value)
    .replace(/\{\{\s*amount(_with_comma_separator|_no_decimals_with_comma_separator)?\s*\}\}/, value);
};

const trapFocus = (container) => {
  const focusable = container.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])');
  if (!focusable.length) return () => {};
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const handler = (e) => {
    if (e.key !== 'Tab') return;
    if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
    else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
  };
  container.addEventListener('keydown', handler);
  first.focus();
  return () => container.removeEventListener('keydown', handler);
};

const showToast = (message) => {
  const container = $('#ToastContainer');
  if (!container) return;
  const t = document.createElement('div');
  t.className = 'toast';
  t.textContent = message;
  container.appendChild(t);
  setTimeout(() => t.remove(), 3500);
};

/* --------------------------------------------------------------------------
   Scroll fade-in via IntersectionObserver
-------------------------------------------------------------------------- */
const initScrollFadeIn = () => {
  const els = $$('.fade-in, [data-fade-in]');
  if (!els.length || !('IntersectionObserver' in window)) {
    els.forEach(el => el.classList.add('is-visible'));
    return;
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);
      }
    });
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });
  // Above-the-fold safety: if an element is already in the viewport at init time,
  // mark it visible immediately. IntersectionObserver is throttled on background tabs,
  // so above-the-fold content might otherwise stay invisible until the user comes back.
  const vh = window.innerHeight;
  els.forEach(el => {
    const rect = el.getBoundingClientRect();
    if (rect.top < vh && rect.bottom > 0) {
      el.classList.add('is-visible');
    } else {
      io.observe(el);
    }
  });
};

/* --------------------------------------------------------------------------
   Header scroll behaviour
-------------------------------------------------------------------------- */
const initHeaderScroll = () => {
  const header = $('[data-site-header]');
  if (!header) return;
  const transparent = header.dataset.transparent === 'true';
  const sticky = header.dataset.sticky === 'true';

  const onScroll = () => {
    const y = window.scrollY;
    if (transparent) {
      if (y > 80) header.classList.remove('site-header--transparent');
      else header.classList.add('site-header--transparent');
    }
    if (sticky) {
      if (y > 80) header.classList.add('site-header--scrolled');
      else header.classList.remove('site-header--scrolled');
    }
  };
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
};

/* --------------------------------------------------------------------------
   Generic drawer open/close
-------------------------------------------------------------------------- */
class Drawer {
  constructor(panelSelector, overlaySelector) {
    this.panel = $(panelSelector);
    this.overlay = overlaySelector ? $(overlaySelector) : null;
    this.releaseFocus = null;
    this.previouslyFocused = null;

    if (this.overlay) this.overlay.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    });
    // Event delegation: works for close buttons that get re-rendered (e.g. cart drawer after add)
    this.panel?.addEventListener('click', (e) => {
      if (e.target.closest('[data-drawer-close]')) this.close();
    });
  }
  isOpen() { return this.panel?.getAttribute('aria-hidden') === 'false'; }
  open() {
    if (!this.panel) return;
    this.previouslyFocused = document.activeElement;
    this.panel.setAttribute('aria-hidden', 'false');
    if (this.overlay) this.overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
    this.releaseFocus = trapFocus(this.panel);
  }
  close() {
    if (!this.panel) return;
    this.panel.setAttribute('aria-hidden', 'true');
    if (this.overlay) this.overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');
    if (this.releaseFocus) this.releaseFocus();
    if (this.previouslyFocused) this.previouslyFocused.focus();
  }
}

/* --------------------------------------------------------------------------
   Cart drawer
-------------------------------------------------------------------------- */
let cartDrawer;
const initCartDrawer = () => {
  cartDrawer = new Drawer('[data-cart-drawer]', '[data-cart-overlay]');
  $$('[data-cart-open]').forEach(btn =>
    btn.addEventListener('click', (e) => { e.preventDefault(); openCartDrawer(); })
  );

  // Quantity buttons
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-cart-qty]');
    if (!btn) return;
    e.preventDefault();
    const line = parseInt(btn.dataset.line, 10);
    const action = btn.dataset.cartQty; // 'inc' | 'dec' | 'remove'
    const input = btn.parentElement.querySelector('input');
    let qty = input ? parseInt(input.value, 10) : 0;
    if (action === 'inc') qty += 1;
    else if (action === 'dec') qty = Math.max(0, qty - 1);
    else if (action === 'remove') qty = 0;
    await updateCartLine(line, qty);
  });
};

const openCartDrawer = async () => {
  await refreshCartDrawer();
  cartDrawer?.open();
};

const refreshCartDrawer = async () => {
  try {
    const res = await fetch(`${window.theme.routes.cart_url}?section_id=cart-drawer`);
    const html = await res.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-cart-drawer-content]');
    const target = $('[data-cart-drawer-content]');
    if (fresh && target) target.innerHTML = fresh.innerHTML;
    bagAfterRender();
    updateCartCount();
  } catch (err) {
    console.error('cart refresh failed', err);
  }
};

// After adding to the bag: stay on the page (no drawer), update the bag count and show a short confirmation
const addedFeedback = async () => {
  await refreshCartDrawer();
  showToast((window.theme && window.theme.strings && window.theme.strings.addedToBag) || 'Added to bag');
};

/* --------------------------------------------------------------------------
   Bag drawer extras: Bag / Wishlist tabs, free shipping bar, "Add a little extra",
   discount codes (Cart AJAX /cart/update.js discount param), wishlist pane
-------------------------------------------------------------------------- */
let bagTab = 'bag';
let bagFlash = null;   // message to show in the discount box after the drawer re-renders
const shopRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

const bagShowTab = (tab) => {
  bagTab = tab;
  $$('[data-bag-tab]').forEach(b => { const on = b.dataset.bagTab === tab; b.classList.toggle('is-active', on); b.setAttribute('aria-selected', on ? 'true' : 'false'); });
  $$('[data-bag-pane]').forEach(p => { p.hidden = p.dataset.bagPane !== tab; });
  const foot = $('[data-bag-foot]'); if (foot) foot.hidden = tab !== 'bag';
  if (tab === 'wishlist') renderBagWishlist();
};

const bagAfterRender = () => {
  // free shipping progress (threshold set in USD, cart total is in the shopper's currency)
  const fs = $('[data-free-ship]');
  if (fs) {
    const rate = Number((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1);
    const goal = Math.round(Number(fs.dataset.thresholdUsd) * 100 * rate);
    const total = Number(fs.dataset.total || 0);
    const left = Math.max(0, goal - total);
    $('[data-free-ship-bar]', fs).style.width = Math.min(100, (total / goal) * 100) + '%';
    $('[data-free-ship-goal]', fs).innerHTML = formatMoney(goal);
    $('[data-free-ship-msg]', fs).innerHTML = left > 0 ? fs.dataset.msgAway.replace('[amount]', formatMoney(left)) : fs.dataset.msgGot;
  }
  // hearts on bag lines reflect the saved wishlist
  const list = getWishlist();
  $$('[data-cart-drawer] [data-wishlist-toggle]').forEach(b => b.setAttribute('aria-pressed', list.includes(b.dataset.wishlistToggle) ? 'true' : 'false'));
  if (bagFlash) {
    const msg = $('[data-bag-discount-msg]');
    if (msg) { const dec = document.createElement('textarea'); dec.innerHTML = bagFlash.text || ''; msg.hidden = false; msg.textContent = dec.value; msg.className = 'bag-discount__msg ' + (bagFlash.ok ? 'is-ok' : 'is-error'); }
    bagFlash = null;
  }
  bagShowTab(bagTab);
};

const bagSetDiscounts = async (codes) => {
  const res = await fetch(shopRoot() + 'cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ discount: codes.join(',') })
  });
  if (!res.ok) throw new Error('discount update failed');
  return res.json();
};

const renderBagWishlist = async () => {
  const box = $('[data-bag-wishlist]');
  if (!box) return;
  const list = getWishlist();
  if (!list.length) { box.innerHTML = ($('[data-bag-wish-empty]') || {}).innerHTML || ''; return; }
  box.innerHTML = '<p class="bag-sub">' + (box.dataset.msgLoading || '') + '</p>';
  const rows = await Promise.all(list.map(async (key) => {
    const m = String(key).match(/^(.*)-(\d{6,})$/);
    const handle = m ? m[1] : key, variantId = m ? Number(m[2]) : null;
    try {
      const p = await fetch(shopRoot() + 'products/' + handle + '.js').then(r => r.ok ? r.json() : null);
      if (!p) return '';
      const v = (variantId && p.variants.find(x => x.id === variantId)) || p.variants[0];
      const img = (v && v.featured_image && v.featured_image.src) || p.featured_image || '';
      const url = shopRoot() + 'products/' + handle + (variantId ? '?variant=' + variantId : '');
      const sub = v && !/default title/i.test(v.title) ? v.title.replace(/ \/ /g, ' - ') : '';
      const esc = (t) => String(t || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      return '<div class="bag-line"><a href="' + url + '" class="bag-line__img">' + (img ? '<img src="' + img + (img.includes('?') ? '&' : '?') + 'width=240" alt="" loading="lazy">' : '') + '</a>'
        + '<div class="bag-line__info"><a href="' + url + '" class="bag-line__title">' + esc(p.title) + '</a>' + (sub ? '<p class="bag-line__variant">' + esc(sub) + '</p>' : '')
        + '<div class="bag-line__row"><p class="bag-line__price">' + formatMoney(v ? v.price : p.price) + '</p><a class="bag-wish__view" href="' + url + '">' + (box.dataset.msgView || 'View') + '</a></div></div>'
        + '<button type="button" class="bag-line__wish" data-wishlist-toggle="' + esc(key) + '" aria-pressed="true" aria-label="Remove from wishlist"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20.25s-7.2-4.35-9.05-8.85C1.6 8.1 3.55 4.5 7.05 4.5c2.05 0 3.6 1.15 4.95 2.95 1.35-1.8 2.9-2.95 4.95-2.95 3.5 0 5.45 3.6 4.1 6.9-1.85 4.5-9.05 8.85-9.05 8.85Z"/></svg></button></div>';
    } catch (e) { return ''; }
  }));
  const html = rows.join('');
  box.innerHTML = html || ($('[data-bag-wish-empty]') || {}).innerHTML || '';
};

const initBag = () => {
  document.addEventListener('click', async (e) => {
    const tab = e.target.closest('[data-bag-tab]');
    if (tab) { bagShowTab(tab.dataset.bagTab); return; }
    const add = e.target.closest('[data-bag-add]');
    if (add) {
      e.preventDefault(); add.classList.add('is-loading');
      try {
        const r = await fetch(window.theme.routes.cart_add_url + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: [{ id: Number(add.dataset.bagAdd), quantity: 1 }] }) });
        if (!r.ok) throw new Error();
        await refreshCartDrawer();
      } catch (err) { showToast('Could not add to bag.'); add.classList.remove('is-loading'); }
      return;
    }
    const rm = e.target.closest('[data-bag-code-remove]');
    if (rm) {
      e.preventDefault();
      try {
        const cart = await fetch(shopRoot() + 'cart.js').then(r => r.json());
        const keep = (cart.discount_codes || []).map(d => d.code).filter(c => c.toUpperCase() !== rm.dataset.bagCodeRemove.toUpperCase());
        await bagSetDiscounts(keep);
        await refreshCartDrawer();
      } catch (err) { showToast('Could not update the code.'); }
      return;
    }
    // a heart in the wishlist pane removes the item there; redraw after the wishlist handler runs
    if (e.target.closest('[data-bag-wishlist] [data-wishlist-toggle]')) setTimeout(renderBagWishlist, 50);
  });
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-bag-discount]');
    if (!form) return;
    e.preventDefault();
    const code = (form.elements.code.value || '').trim();
    if (!code) return;
    const btn = form.querySelector('button'); btn.disabled = true;
    try {
      const cart = await fetch(shopRoot() + 'cart.js').then(r => r.json());
      const current = (cart.discount_codes || []).map(d => d.code);
      const after = await bagSetDiscounts([...new Set([...current, code])]);
      const hit = (after.discount_codes || []).find(d => d.code.toUpperCase() === code.toUpperCase());
      if (!hit || !hit.applicable) {
        await bagSetDiscounts(current);   // leave the bag as it was
        bagFlash = { ok: false, text: form.dataset.msgBad };
      } else bagFlash = { ok: true, text: form.dataset.msgOk };
      await refreshCartDrawer();
    } catch (err) { showToast('Could not apply the code.'); btn.disabled = false; }
  });
  bagAfterRender();
};

/* --------------------------------------------------------------------------
   Phones: buy bar + "Add to bag" size sheet (Gymshark style)
-------------------------------------------------------------------------- */
const initMobileBuy = () => {
  const sheet = $('[data-msheet]');
  const overlay = $('[data-msheet-overlay]');
  // header search icon opens the menu with its search field focused
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-focus-search]')) setTimeout(() => { const i = $('[data-menu-drawer] input[type="search"]'); if (i) i.focus(); }, 350);
  });
  if (!sheet) return;
  const variants = window.productVariants || [];
  if (!variants.length) return;
  const colorIdx = window.productColorOptionIndex;
  const nOpts = (variants[0].options || []).length;
  // the size option is the one that is not colour (products with one non-colour option use it as size)
  let sizeIdx = -1;
  for (let i = 0; i < nOpts; i++) if (i !== colorIdx) { sizeIdx = i; break; }
  if (nOpts === 1 && colorIdx === 0) sizeIdx = -1;
  const form = $('[data-product-form]');
  const esc = (t) => String(t || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const currentColor = () => {
    if (colorIdx == null || colorIdx < 0) return null;
    const on = form && form.querySelector(`[data-option-index="${colorIdx + 1}"][aria-checked="true"]`);
    if (on) return on.dataset.optionValue;
    const urlV = new URL(location.href).searchParams.get('variant');
    const v = variants.find(x => String(x.id) === urlV) || variants.find(x => x.available) || variants[0];
    return v.options[colorIdx];
  };
  const close = () => { sheet.hidden = true; overlay.hidden = true; document.documentElement.style.overflow = ''; };
  const addVariant = async (id, btn) => {
    if (btn) btn.classList.add('is-loading');
    try {
      const r = await fetch(window.theme.routes.cart_add_url + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: [{ id, quantity: 1 }] }) });
      if (!r.ok) { const er = await r.json().catch(() => ({})); throw new Error(er.description || 'Could not add to bag.'); }
      close();
      await addedFeedback();
    } catch (err) { showToast(err.message || 'Could not add to bag.'); }
    finally { if (btn) btn.classList.remove('is-loading'); }
  };
  const open = () => {
    const color = currentColor();
    const inColor = variants.filter(v => color == null || v.options[colorIdx] === color);
    if (sizeIdx < 0) {   // nothing to choose: add the variant straight away
      const v = inColor.find(x => x.available);
      if (v) addVariant(v.id); else showToast(sheet.dataset.txtSold);
      return;
    }
    const lead = inColor.find(v => v.featured_image) || inColor[0];
    const img = (lead && lead.featured_image) || sheet.dataset.image;
    const meta = [sheet.dataset.fit, color].filter(Boolean).join(' · ');
    const price = (inColor.find(v => v.available) || inColor[0]).price;
    $('[data-msheet-product]', sheet).innerHTML =
      '<div class="m-sheet__img">' + (img ? '<img src="' + esc(img) + '" alt="">' : '') + '</div>'
      + '<div class="m-sheet__info">' + (sheet.dataset.new ? '<span class="m-sheet__badge">' + esc(sheet.dataset.new) + '</span>' : '')
      + '<span class="m-sheet__title">' + esc(sheet.dataset.title) + '</span>'
      + (meta ? '<span class="m-sheet__meta">' + esc(meta) + '</span>' : '')
      + '<span class="m-sheet__price">' + formatMoney(price) + '</span></div>';
    const seen = new Set();
    $('[data-msheet-sizes]', sheet).innerHTML = inColor.filter(v => { const k = v.options[sizeIdx]; if (seen.has(k)) return false; seen.add(k); return true; })
      .map(v => '<li><button type="button" class="m-size" data-msize="' + v.id + '"' + (v.available ? '' : ' disabled') + '><span>' + esc(v.options[sizeIdx]) + '</span>'
        + '<span class="m-size__act">' + (v.available ? esc(sheet.dataset.txtAdd) + ' <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>' : esc(sheet.dataset.txtSold)) + '</span></button></li>').join('');
    sheet.hidden = false; overlay.hidden = false; document.documentElement.style.overflow = 'hidden';
  };
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-mbuy-open]')) { e.preventDefault(); open(); return; }
    if (e.target.closest('[data-msheet-close]') || e.target === overlay) { close(); return; }
    const sz = e.target.closest('[data-msize]');
    if (sz && !sz.disabled) addVariant(Number(sz.dataset.msize), sz);
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !sheet.hidden) close(); });
};

const updateCartCount = async () => {
  try {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    const cart = await res.json();
    $$('[data-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
  } catch (err) {
    console.warn('count refresh failed', err);
  }
};

const updateCartLine = async (line, quantity) => {
  try {
    const res = await fetch(window.theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    if (!res.ok) throw new Error('cart change failed');
    await refreshCartDrawer();
  } catch (err) {
    console.error(err);
    showToast('Could not update cart.');
  }
};

const addToCart = async (formData) => {
  const res = await fetch(window.theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Accept': 'application/json' },
    body: formData
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.description || 'Add to cart failed');
  }
  return res.json();
};

/* --------------------------------------------------------------------------
   Menu drawer
-------------------------------------------------------------------------- */
const initMenuDrawer = () => {
  const drawer = new Drawer('[data-menu-drawer]', '[data-menu-overlay]');
  $$('[data-menu-open]').forEach(btn => btn.addEventListener('click', () => drawer.open()));
};

/* --------------------------------------------------------------------------
   Search drawer with predictive search
-------------------------------------------------------------------------- */
const initSearchDrawer = () => {
  const drawer = new Drawer('[data-search-drawer]');
  $$('[data-search-open]').forEach(btn => btn.addEventListener('click', () => {
    drawer.open();
    setTimeout(() => $('[data-search-input]')?.focus(), 100);
  }));

  const input = $('[data-search-input]');
  const results = $('[data-search-results]');
  if (!input || !results) return;

  let debounce;
  input.addEventListener('input', (e) => {
    const q = e.target.value.trim();
    clearTimeout(debounce);
    if (q.length < 2) { results.innerHTML = ''; return; }
    debounce = setTimeout(() => runPredictiveSearch(q, results), 200);
  });
};

const runPredictiveSearch = async (q, container) => {
  try {
    const url = `${window.theme.routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product,collection,page&resources[limit]=6&section_id=predictive-search`;
    const res = await fetch(url);
    const html = await res.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-search-results-inner]');
    container.innerHTML = fresh ? fresh.innerHTML : '';
  } catch (err) {
    console.error('predictive search failed', err);
  }
};

/* --------------------------------------------------------------------------
   Quick add buttons on product cards
-------------------------------------------------------------------------- */
const initQuickAdd = () => {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    const variantId = btn.dataset.quickAdd;
    if (!variantId) return;
    btn.disabled = true;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    try {
      const fd = new FormData();
      fd.append('id', variantId);
      fd.append('quantity', '1');
      await addToCart(fd);
      await addedFeedback();
    } catch (err) {
      showToast(err.message || 'Could not add to bag.');
    } finally {
      setTimeout(() => {
        btn.disabled = false;
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      }, 600);
    }
  });
};

/* --------------------------------------------------------------------------
   Product form
-------------------------------------------------------------------------- */
const initProductForm = () => {
  const form = $('[data-product-form]');
  if (!form) return;

  const submit = form.querySelector('[type="submit"]');
  // Remember the button's add text, but not when the page opened on a sold-out variant: then the
  // rendered text is "Sold out", and switching to an in-stock colour would show it on a live button
  if (submit && !submit.dataset.originalText && (!submit.disabled || submit.hasAttribute('data-preorder-closed'))) {
    submit.dataset.originalText = submit.textContent;
  }

  // On load, sync ATC state with current selection (size is never pre-selected
  // even when ?variant=ID is in URL, so most loads will be in 'partial' state).
  updateVariantFromOptions(form);

  // Variant picker. Colour click → instant in-page switch: mark the swatch, re-check which sizes
  // exist in that colour, put ?variant= in the address bar (no reload) and scroll the gallery to
  // that colour's photo. Other options (size etc.) → instant client-side update as before.
  const refreshSizesForColor = (colorValue) => {
    const colorIdx = window.productColorOptionIndex;
    const variants = window.productVariants || [];
    $$('.option-pill[data-option-index]', form).forEach(p => {
      const idx = parseInt(p.dataset.optionIndex, 10) - 1;
      if (idx === colorIdx) return;
      const ok = variants.some(v => v.options[idx] === p.dataset.optionValue && v.available && v.options[colorIdx] === colorValue);
      p.classList.toggle('is-unavailable', !ok);
      if (ok) p.removeAttribute('aria-disabled'); else p.setAttribute('aria-disabled', 'true');
      let bell = p.querySelector('.option-pill__notify');
      if (!ok && !bell) {
        bell = document.createElement('span'); bell.className = 'option-pill__notify'; bell.setAttribute('aria-hidden', 'true');
        bell.innerHTML = '<svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 3a6 6 0 0 0-6 6v3.6L4.3 15.4A1 1 0 0 0 5.2 17h13.6a1 1 0 0 0 .9-1.6L18 12.6V9a6 6 0 0 0-6-6Zm-2.5 15.5a2.5 2.5 0 0 0 5 0z"/></svg>';
        p.prepend(bell);
      } else if (ok && bell) bell.remove();
    });
  };

  $$('[data-option-value]', form).forEach(pill => {
    pill.addEventListener('click', (e) => {
      const optionIndex = parseInt(pill.dataset.optionIndex, 10);
      const colorIdx = window.productColorOptionIndex;
      const isColor = colorIdx != null && colorIdx >= 0 && (optionIndex - 1) === colorIdx;

      if (isColor) {
        e.preventDefault();
        const colorValue = pill.dataset.optionValue;
        const variants = window.productVariants || [];
        const match =
          variants.find(v => v.options[colorIdx] === colorValue && v.available) ||
          variants.find(v => v.options[colorIdx] === colorValue);
        if (!match) return;
        $$(`[data-option-index="${optionIndex}"]`, form).forEach(p => {
          p.setAttribute('aria-checked', 'false');
          p.classList.remove('is-selected');
        });
        pill.setAttribute('aria-checked', 'true');
        pill.classList.add('is-selected');
        const display = form.querySelector(`[data-option-display-${optionIndex}]`);
        if (display) display.textContent = colorValue;
        refreshSizesForColor(colorValue);
        updateVariantFromOptions(form);
        // Keep the market/locale path exactly as it is; only swap ?variant= (shareable, survives refresh)
        const url = new URL(window.location.href);
        url.searchParams.set('variant', match.id);
        url.hash = '';
        history.replaceState(null, '', url.pathname + url.search);
        swapGalleryLeadImage(colorValue);
        return;
      }

      // Non-color option (size etc.): in-page update only.
      $$(`[data-option-index="${optionIndex}"]`, form).forEach(p => {
        p.setAttribute('aria-checked', 'false');
        p.classList.remove('is-selected');
      });
      pill.setAttribute('aria-checked', 'true');
      pill.classList.add('is-selected');
      const display = form.querySelector(`[data-option-display-${optionIndex}]`);
      if (display) display.textContent = pill.dataset.optionValue;
      updateVariantFromOptions(form);
    });
  });

  // On load: if URL has #scroll-to-color and a ?variant=ID, scroll the gallery
  // to that variant's image.
  if (window.location.hash === '#scroll-to-color') {
    const urlVariant = new URL(window.location.href).searchParams.get('variant');
    const variants = window.productVariants || [];
    const match = variants.find(v => String(v.id) === String(urlVariant));
    if (match && match.options && window.productColorOptionIndex >= 0) {
      // Defer until images have layout
      requestAnimationFrame(() => {
        swapGalleryLeadImage(match.options[window.productColorOptionIndex]);
        // Clean the hash so refreshes don't keep scrolling
        history.replaceState(null, '', window.location.pathname + window.location.search);
      });
    }
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const submitBtn = form.querySelector('[type="submit"]');
    submitBtn.disabled = true;
    const original = submitBtn.textContent;
    submitBtn.textContent = window.theme.strings.adding;
    try {
      await addToCart(new FormData(form));
      submitBtn.textContent = window.theme.strings.added;
      await addedFeedback();
    } catch (err) {
      showToast(err.message || 'Could not add to bag.');
    } finally {
      setTimeout(() => { submitBtn.disabled = false; submitBtn.textContent = original; }, 1400);
    }
  });

  // Sticky ATC visibility
  const atc = $('[data-sticky-atc]');
  if (atc) {
    const stickyTrigger = $('[data-sticky-trigger]') || form;
    const io = new IntersectionObserver(([entry]) => {
      atc.classList.toggle('is-active', !entry.isIntersecting);
    }, { rootMargin: '-200px 0px 0px 0px' });
    io.observe(stickyTrigger);
  }
};

const updateVariantFromOptions = (form) => {
  const variants = window.productVariants || [];
  const totalOptions = $$('[data-option-index]', form).reduce((set, p) => set.add(p.dataset.optionIndex), new Set()).size;
  const selected = [];
  for (let i = 1; i <= 3; i++) {
    const checked = form.querySelector(`[data-option-index="${i}"][aria-checked="true"]`);
    if (checked) selected.push(checked.dataset.optionValue);
  }
  const submit = form.querySelector('[type="submit"]');
  const idInput = form.querySelector('[name="id"]');

  // Not every option is chosen yet → keep the form locked (says Sold out when no variant is available at all)
  if (selected.length < totalOptions) {
    if (idInput) idInput.value = '';
    if (submit) {
      submit.disabled = true;
      submit.textContent = variants.length && !variants.some(v => v.available)
        ? window.theme.strings.soldOut
        : (window.theme.strings && window.theme.strings.selectSize) || 'Select a size';
    }
    return;
  }

  const match = variants.find(v =>
    v.options.length === selected.length && v.options.every((opt, idx) => opt === selected[idx])
  );
  if (!match) {
    if (idInput) idInput.value = '';
    if (submit) {
      submit.disabled = true;
      submit.textContent = window.theme.strings.unavailable || 'Unavailable';
    }
    return;
  }
  if (idInput) idInput.value = match.id;
  const priceEl = $('[data-product-price]');
  if (priceEl) priceEl.innerHTML = formatMoney(match.price);
  if (submit) {
    submit.disabled = !match.available;
    submit.textContent = match.available
      ? (submit.dataset.originalText || window.theme.strings.addToCart)
      : window.theme.strings.soldOut;
  }
};

/* Scroll the gallery to the chosen color's variant image.
   Match by Shopify image.id (data-image-id on each <figure>) — robust against
   CDN URL transforms. */
const swapGalleryLeadImage = (colorValue) => {
  const colorIdx = window.productColorOptionIndex;
  const variants = window.productVariants || [];
  if (colorIdx == null || colorIdx < 0) return;
  const match = variants.find(v => v.options[colorIdx] === colorValue && v.featured_image_id);
  if (!match) return;
  const cell = document.querySelector(`.product-gallery__cell[data-image-id="${match.featured_image_id}"]`);
  if (cell) {
    cell.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

/* --------------------------------------------------------------------------
   Product gallery — click thumb to swap main
-------------------------------------------------------------------------- */
const initProductGallery = () => {
  const gallery = $('[data-product-gallery]');
  if (!gallery) return;
  const main = gallery.querySelector('[data-gallery-main]');
  const slides = $$('[data-gallery-slide]', gallery);
  $$('[data-gallery-thumb]', gallery).forEach((thumb, idx) => {
    thumb.addEventListener('click', () => {
      $$('[data-gallery-thumb]', gallery).forEach(t => t.removeAttribute('aria-current'));
      thumb.setAttribute('aria-current', 'true');
      const target = slides[idx];
      if (target && main) main.scrollTo({ top: target.offsetTop - main.offsetTop, behavior: 'smooth' });
    });
  });

  // Mobile swipe dots
  const mosaic = gallery.querySelector('.product-gallery--mosaic');
  if (mosaic) {
    const cells = Array.from(mosaic.querySelectorAll('.product-gallery__cell'));
    if (cells.length > 1) {
      const dotsWrap = document.createElement('div');
      dotsWrap.className = 'product-gallery-dots';
      cells.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'product-gallery-dots__dot' + (i === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', 'Image ' + (i + 1));
        dot.addEventListener('click', () => {
          cells[i].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        });
        dotsWrap.appendChild(dot);
      });
      mosaic.insertAdjacentElement('afterend', dotsWrap);
      const dots = Array.from(dotsWrap.children);
      const updateActive = () => {
        const x = mosaic.scrollLeft;
        const w = mosaic.clientWidth || 1;
        const idx = Math.round(x / w);
        dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
      };
      mosaic.addEventListener('scroll', updateActive, { passive: true });
      window.addEventListener('resize', updateActive);
    }
  }

  initGalleryDesktop(gallery);
  initOpenTabLinks();
};

/* Animated window scroll (rAF, ease-out). Used instead of behavior:'smooth' so it
   works even when html { scroll-behavior } or an in-flight scroll would swallow it. */
const animateScrollTo = (targetY, duration = 420) => {
  const startY = window.scrollY;
  const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  const endY = Math.max(0, Math.min(maxY, targetY));
  const delta = endY - startY;
  if (Math.abs(delta) < 2) return;
  const t0 = performance.now();
  const ease = (x) => 1 - Math.pow(1 - x, 3);
  const step = () => {
    const k = Math.min(1, (performance.now() - t0) / duration);
    window.scrollTo({ top: startY + delta * ease(k), behavior: 'instant' });
    if (k < 1) setTimeout(step, 16);
  };
  step();
};

/* --------------------------------------------------------------------------
   Product gallery (desktop): sticky column, +/- cursor, click-to-zoom, side nav
   Gymshark-style. Everything here is a no-op under 901px or on touch devices.
-------------------------------------------------------------------------- */
const initGalleryDesktop = (gallery) => {
  const col = gallery.querySelector('[data-gallery-col]');
  const mosaic = gallery.querySelector('[data-gallery-mosaic]');
  if (!col || !mosaic) return;
  const cells = $$('[data-gallery-cell]', mosaic);
  const cursor = $('[data-gallery-cursor]');
  const glyph = $('[data-gallery-cursor-glyph]');
  const zoom = gallery.querySelector('[data-gallery-zoom]');
  const zoomImg = gallery.querySelector('[data-gallery-zoom-img]');
  const nav = gallery.querySelector('[data-gallery-nav]');
  const bar = gallery.querySelector('[data-gallery-bar]');
  const prevBtn = gallery.querySelector('[data-gallery-prev]');
  const nextBtn = gallery.querySelector('[data-gallery-next]');
  const desktop = window.matchMedia('(min-width: 901px)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const isDesktop = () => desktop.matches;

  /* Header height -> CSS var; gallery sticky offset:
     shorter than the viewport -> sticks under the header,
     taller -> sticks when its bottom reaches the viewport bottom. */
  const headerBottom = () => {
    const h = $('.site-header');
    if (!h) return 0;
    const r = h.getBoundingClientRect();
    return Math.max(0, Math.round(r.bottom));
  };
  const layout = () => {
    if (!isDesktop()) { col.style.removeProperty('--pdp-gallery-top'); return; }
    const hb = headerBottom();
    document.documentElement.style.setProperty('--pdp-header-h', hb + 'px');
    const top = Math.min(hb, window.innerHeight - col.offsetHeight);
    col.style.setProperty('--pdp-gallery-top', Math.round(top) + 'px');
  };
  layout();
  window.addEventListener('resize', layout);
  let layoutQueued = false;
  window.addEventListener('scroll', () => {
    if (layoutQueued) return;
    layoutQueued = true;
    requestAnimationFrame(() => { layout(); layoutQueued = false; });
  }, { passive: true });
  if ('ResizeObserver' in window) new ResizeObserver(layout).observe(mosaic);

  /* Gymshark-style wheel zones (desktop). Photos and info are each their own scroll box, as tall as the
     screen under the sticky header (base.css). Over the photos the photos scroll, over the info the info
     scrolls, in the gap between them both scroll. Whichever side you are on, once that side has reached
     its end the page carries on to the next section; it never waits for the other side. */
  const info = gallery.querySelector('.product-info');
  const stickyTop = () => { const h = $('.site-header'); return h ? h.offsetHeight : 0; };
  const setSticky = () => document.documentElement.style.setProperty('--pdp-sticky-top', stickyTop() + 'px');
  setSticky();
  window.addEventListener('resize', setSticky);
  if (info) gallery.addEventListener('wheel', (e) => {
    if (!isDesktop() || e.ctrlKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
    if (e.target.closest('.product-gallery__zoom, dialog, [role="dialog"]')) return;
    const dy = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? window.innerHeight : 1);
    const room = (el) => (dy > 0 ? el.scrollHeight - el.clientHeight - el.scrollTop : el.scrollTop) > 0.5;
    const page = (d) => { e.preventDefault(); window.scrollBy({ top: d, behavior: 'instant' }); };
    const own = (el) => { if (el.contains(e.target)) return; e.preventDefault(); el.scrollTop += dy; };   // native scroll when the pointer is inside it
    // the section is only partly on screen: move the page until it sits under the header first
    const off = gallery.getBoundingClientRect().top - stickyTop();
    if (off > 0.5) return page(dy > 0 ? Math.min(dy, off) : dy);
    if (off < -0.5) return page(dy < 0 ? Math.max(dy, off) : dy);
    const x = e.clientX;
    const textLeft = info.getBoundingClientRect().left + (parseFloat(getComputedStyle(info).paddingLeft) || 0);
    if (x < col.getBoundingClientRect().right - 40) return room(col) ? own(col) : page(dy);     // photos
    if (x >= textLeft) return room(info) ? own(info) : page(dy);                              // info
    if (!room(col) || !room(info)) return page(dy);                                          // the gap: both
    e.preventDefault(); col.scrollTop += dy; info.scrollTop += dy;
  }, { passive: false });

  /* The nav steps through gallery ROWS (two half images share a row).
     Current row = the row whose centre is nearest the viewport centre. */
  let rows = [];
  const buildRows = () => {
    rows = [];
    cells.forEach(c => {
      const top = Math.round(c.offsetTop);
      const row = rows.find(r => Math.abs(r.top - top) < 3);
      if (row) row.cells.push(c); else rows.push({ top, cells: [c] });
    });
    rows.sort((a, b) => a.top - b.top);
  };
  buildRows();
  let current = 0;
  const findCurrent = () => {
    const mid = window.innerHeight / 2;
    let best = 0, bestD = Infinity;
    rows.forEach((row, i) => {
      const r = row.cells[0].getBoundingClientRect();
      const d = Math.abs((r.top + r.bottom) / 2 - mid);
      if (d < bestD) { bestD = d; best = i; }
    });
    return best;
  };
  const paintNav = () => {
    if (!nav || !bar || rows.length < 2) return;
    bar.style.height = (100 / rows.length) + '%';
    bar.style.top = (current * 100 / rows.length) + '%';
    if (prevBtn) prevBtn.disabled = current === 0;
    if (nextBtn) nextBtn.disabled = current === rows.length - 1;
  };
  const goTo = (i) => {
    const row = rows[Math.max(0, Math.min(rows.length - 1, i))];
    if (!row) return;
    if (isDesktop() && col.scrollHeight > col.clientHeight + 1) {   // desktop: the photos scroll inside their column
      const from = col.scrollTop, to = Math.max(0, Math.min(col.scrollHeight - col.clientHeight, from + row.cells[0].getBoundingClientRect().top - col.getBoundingClientRect().top));
      const t0 = performance.now(), ease = (k) => 1 - Math.pow(1 - k, 3);
      const step = () => { const k = Math.min(1, (performance.now() - t0) / 420); col.scrollTop = from + (to - from) * ease(k); if (k < 1) setTimeout(step, 16); };
      return step();
    }
    const top = row.cells[0].getBoundingClientRect().top + window.scrollY - headerBottom() - 4;
    animateScrollTo(top);
  };
  if (prevBtn) prevBtn.addEventListener('click', () => { current = findCurrent(); goTo(current - 1); });
  if (nextBtn) nextBtn.addEventListener('click', () => { current = findCurrent(); goTo(current + 1); });
  window.addEventListener('resize', () => { buildRows(); current = findCurrent(); paintNav(); });

  /* Show the side nav only while the gallery is on screen (desktop) */
  if (nav && rows.length > 1) {
    const io = new IntersectionObserver(([entry]) => {
      nav.hidden = !(entry.isIntersecting && isDesktop());
    }, { threshold: 0.05 });
    io.observe(mosaic);
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        current = findCurrent();
        paintNav();
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    col.addEventListener('scroll', onScroll, { passive: true });
    current = findCurrent();
    paintNav();
  }

  /* Custom +/- cursor following the pointer over the gallery or the zoom overlay */
  const cursorOn = () => finePointer.matches && isDesktop() && cursor;
  const moveCursor = (e) => {
    if (!cursorOn()) return;
    cursor.style.transform = `translate(${e.clientX}px, ${e.clientY}px) translate(-50%, -50%)`;
    cursor.classList.add('is-visible');
  };
  const hideCursor = () => cursor && cursor.classList.remove('is-visible');
  [mosaic, zoom].forEach(el => {
    if (!el) return;
    el.addEventListener('mousemove', moveCursor);
    el.addEventListener('mouseenter', moveCursor);
    el.addEventListener('mouseleave', hideCursor);
  });

  /* Click-to-zoom: the clicked image fills the gallery column at 2x and pans with the pointer */
  if (!zoom || !zoomImg) return;
  let zoomed = false;
  const pan = (clientX, clientY) => {
    const box = zoom.getBoundingClientRect();
    const px = Math.min(1, Math.max(0, (clientX - box.left) / box.width));
    const py = Math.min(1, Math.max(0, (clientY - box.top) / box.height));
    const dx = Math.max(0, zoomImg.offsetWidth - box.width);
    const dy = Math.max(0, zoomImg.offsetHeight - box.height);
    zoomImg.style.transform = `translate(${-px * dx}px, ${-py * dy}px)`;
  };
  const openZoom = (cell, e) => {
    const src = cell.dataset.zoomSrc || cell.querySelector('img')?.currentSrc;
    if (!src) return;
    zoomImg.src = src;
    zoomImg.alt = cell.dataset.zoomAlt || '';
    zoom.hidden = false;
    zoom.setAttribute('aria-hidden', 'false');
    zoomed = true;
    if (glyph) glyph.textContent = '\u2212';
    const place = () => pan(e.clientX, e.clientY);
    if (zoomImg.complete && zoomImg.naturalWidth) place();
    else zoomImg.addEventListener('load', place, { once: true });
  };
  const closeZoom = () => {
    if (!zoomed) return;
    zoom.hidden = true;
    zoom.setAttribute('aria-hidden', 'true');
    zoomed = false;
    if (glyph) glyph.textContent = '+';
  };
  cells.forEach(cell => {
    cell.addEventListener('click', (e) => {
      if (!isDesktop()) return;
      e.preventDefault();
      openZoom(cell, e);
    });
  });
  zoom.addEventListener('mousemove', (e) => pan(e.clientX, e.clientY));
  zoom.addEventListener('click', closeZoom);
  zoom.addEventListener('wheel', closeZoom, { passive: true });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeZoom(); });
  desktop.addEventListener('change', () => { closeZoom(); layout(); });
};

/* Countdown: [data-countdown][data-countdown-at="ISO date"] ticks every second */
const initCountdown = () => {
  $$('[data-countdown]').forEach(box => {
    const end = new Date(box.dataset.countdownAt);
    if (isNaN(end.getTime())) return;
    const label = box.querySelector('.product-countdown__label');
    const dateEl = box.querySelector('[data-countdown-date]');
    const parts = ['days', 'hours', 'minutes', 'seconds'].map(k => box.querySelector(`[data-countdown-${k}]`));
    // Show the closing date as written in the tag (not converted to the viewer's timezone)
    if (dateEl) {
      const m = String(box.dataset.countdownAt).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) {
        const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
        try {
          dateEl.textContent = d.toLocaleDateString(document.documentElement.lang || 'en', {
            weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC'
          });
        } catch (e) { dateEl.textContent = m[3] + '/' + m[2] + '/' + m[1]; }
      }
    }
    const pad = (n) => String(n).padStart(2, '0');
    let timer = null;
    const tick = () => {
      let diff = Math.floor((end.getTime() - Date.now()) / 1000);
      if (diff <= 0) {
        box.classList.add('is-ended');
        if (label) label.textContent = box.dataset.countdownEnded || label.textContent;
        // the pre-order is closed: stop new orders on this page (server side does the same on the next load)
        const closedText = box.dataset.countdownEnded || 'Pre-order closed';
        document.querySelectorAll('[data-product-form] [type="submit"], .m-buy__add').forEach(b => { b.disabled = true; b.textContent = closedText; });
        if (timer) clearInterval(timer);
        return;
      }
      const d = Math.floor(diff / 86400); diff -= d * 86400;
      const h = Math.floor(diff / 3600); diff -= h * 3600;
      const m = Math.floor(diff / 60); const s = diff - m * 60;
      [d, h, m, s].forEach((v, i) => { if (parts[i]) parts[i].textContent = pad(v); });
    };
    tick();
    timer = setInterval(tick, 1000);
  });
};

/* Estimated delivery: "Order within 5h 12m to get it by Sat 18 Oct" in the visitor's Gulf time zone */
const initDeliveryEstimate = () => {
  // Fixed per-product date (tag delivery-date:YYYY-MM-DD): show it in the page language
  $$('[data-delivery-fixed]').forEach(box => {
    const el = box.querySelector('[data-delivery-fixed-text]');
    const m = String(box.dataset.deliveryFixed).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!el || !m) return;
    try {
      el.textContent = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).toLocaleDateString((document.documentElement.lang || 'en') === 'en' ? 'en-GB' : document.documentElement.lang, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
    } catch (e) {}
  });
  const ZONES = { OM: 'Asia/Muscat', AE: 'Asia/Dubai', SA: 'Asia/Riyadh', QA: 'Asia/Qatar', KW: 'Asia/Kuwait', BH: 'Asia/Bahrain' };
  $$('[data-delivery-estimate]').forEach(box => {
    const text = box.querySelector('[data-delivery-text]');
    if (!text) return;
    const days = Math.max(0, parseInt(box.dataset.days, 10) || 5);
    const daysMin = Math.min(days, Math.max(0, parseInt(box.dataset.daysMin, 10) || days));
    const cutoff = Math.min(24, Math.max(1, parseInt(box.dataset.cutoff, 10) || 24));
    const zone = ZONES[box.dataset.country] || 'Asia/Muscat';
    const docLang = document.documentElement.lang || 'en';
    const lang = docLang === 'en' ? 'en-GB' : docLang;
    const tpl = box.dataset.template || 'Order within [time] to get it between [start] and [date]';
    const uh = box.dataset.unitH || 'h', um = box.dataset.unitM || 'm';
    let fmt;
    try {
      fmt = new Intl.DateTimeFormat('en-GB', { timeZone: zone, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23' });
    } catch (e) { return; }
    const render = () => {
      const p = {};
      fmt.formatToParts(new Date()).forEach(x => { p[x.type] = parseInt(x.value, 10); });
      const nowSec = p.hour * 3600 + p.minute * 60 + p.second;
      let orderDayOffset = 0, remaining = cutoff * 3600 - nowSec;
      if (remaining <= 0) { orderDayOffset = 1; remaining += 86400; }
      const fmtDay = (n) => {
        const d = new Date(Date.UTC(p.year, p.month - 1, p.day + orderDayOffset + n));
        try { return d.toLocaleDateString(lang, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }); }
        catch (e) { return d.toISOString().slice(0, 10); }
      };
      const dateStr = fmtDay(days), startStr = fmtDay(daysMin);
      const h = Math.floor(remaining / 3600), m = Math.max(1, Math.ceil((remaining % 3600) / 60));
      const timeStr = h > 0 ? `${h}${uh} ${m === 60 ? 0 : m}${um}` : `${m}${um}`;
      text.innerHTML = tpl
        .replace('[time]', `<strong>${timeStr}</strong>`)
        .replace('[start]', `<strong>${startStr}</strong>`)
        .replace('[date]', `<strong>${dateStr}</strong>`);
    };
    render();
    setInterval(render, 30000);
    // Hide for sold-out variants: follow the add to cart button state
    const form = box.closest('form') || document;
    const btn = form.querySelector('.product-actions__add');
    if (btn) {
      const soldOut = (box.dataset.soldOut || '').trim().toLowerCase();
      const sync = () => { box.hidden = !!soldOut && btn.disabled && btn.textContent.trim().toLowerCase() === soldOut; };
      new MutationObserver(sync).observe(btn, { attributes: true, attributeFilter: ['disabled'], childList: true, characterData: true, subtree: true });
      sync();
    }
  });
};

/* "Learn more" / "Size guide": open the matching tab and scroll to it */
const initOpenTabLinks = () => {
  $$('[data-open-tab]').forEach(link => {
    link.addEventListener('click', (e) => {
      const id = (link.getAttribute('href') || '').replace(/^#/, '');
      const target = id && document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      if (target.tagName === 'DETAILS') target.open = true;
      const header = $('.site-header');
      const offset = header ? header.getBoundingClientRect().bottom + 16 : 100;
      animateScrollTo(target.getBoundingClientRect().top + window.scrollY - offset);
    });
  });
};

/* --------------------------------------------------------------------------
   Wishlist (localStorage)
-------------------------------------------------------------------------- */
const WISHLIST_KEY = 'saqr_wishlist';
const getWishlist = () => {
  try { return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]'); } catch { return []; }
};
const setWishlist = (list) => localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));

const initWishlist = () => {
  const refresh = () => {
    const list = getWishlist();
    $$('[data-wishlist-toggle]').forEach(btn => {
      const handle = btn.dataset.wishlistToggle;
      btn.setAttribute('aria-pressed', list.includes(handle) ? 'true' : 'false');
    });
    $$('[data-wishlist-count]').forEach(el => { el.textContent = list.length; });
  };
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    const handle = btn.dataset.wishlistToggle;
    let list = getWishlist();
    if (list.includes(handle)) list = list.filter(h => h !== handle);
    else list.push(handle);
    setWishlist(list);
    refresh();
  });
  refresh();
};

/* --------------------------------------------------------------------------
   Announcement bar — rotation + dismiss
-------------------------------------------------------------------------- */
const initAnnouncement = () => {
  const bar = $('[data-announcement]');
  if (!bar) return;
  const messages = $$('[data-announcement-message]', bar);
  const interval = parseInt(bar.dataset.interval, 10) || 5000;
  if (messages.length > 1) {
    let idx = 0;
    setInterval(() => {
      messages[idx].classList.remove('is-active');
      idx = (idx + 1) % messages.length;
      messages[idx].classList.add('is-active');
    }, interval);
  }
  $('[data-announcement-close]', bar)?.addEventListener('click', () => {
    bar.style.display = 'none';
    sessionStorage.setItem('saqr_ann_dismissed', '1');
  });
  if (sessionStorage.getItem('saqr_ann_dismissed') === '1') {
    bar.style.display = 'none';
  }
};

/* --------------------------------------------------------------------------
   Collection — filters & sort (auto-submit)
-------------------------------------------------------------------------- */
const initCollectionFilters = () => {
  // Mobile filter drawer
  const drawer = document.querySelector('[data-facets-drawer]');
  const openBtn = document.querySelector('[data-open-facets]');
  const closeBtn = document.querySelector('[data-close-facets]');
  if (drawer && openBtn) {
    const open = () => { drawer.classList.add('is-open'); document.body.classList.add('facets-open'); };
    const close = () => { drawer.classList.remove('is-open'); document.body.classList.remove('facets-open'); };
    openBtn.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    document.addEventListener('click', (e) => {
      if (document.body.classList.contains('facets-open') &&
          !drawer.contains(e.target) && !openBtn.contains(e.target)) close();
    });
  }

  // Mobile view toggle (grid vs single)
  const grid = document.querySelector('.products-grid');
  document.querySelectorAll('.mobile-view-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const view = btn.dataset.view;
      document.querySelectorAll('.mobile-view-btn').forEach((b) => {
        b.classList.toggle('is-active', b === btn);
        b.setAttribute('aria-pressed', String(b === btn));
      });
      if (grid) grid.classList.toggle('view-single', view === 'single');
    });
  });

  const form = $('[data-facet-form]');
  if (!form) return;
  form.addEventListener('change', () => {
    const params = new URLSearchParams(new FormData(form));
    const url = `${window.location.pathname}?${params.toString()}`;
    window.location.href = url;
  });
  document.querySelectorAll('[data-sort-value]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', btn.dataset.sortValue);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  });
  const sortMenu = document.querySelector('.sort-dropdown');
  if (sortMenu) {
    document.addEventListener('click', (e) => { if (sortMenu.open && !sortMenu.contains(e.target)) sortMenu.open = false; });
  }
  const sort = $('[data-sort-select]');
  if (sort) {
    sort.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      window.location.href = url.toString();
    });
  }

  // Relevancy → randomize on the client (Shopify can't randomize server-side)
  const params = new URLSearchParams(window.location.search);
  const sortBy = params.get('sort_by') || '';
  const isRelevancy = sortBy === '' || sortBy === 'manual';
  if (isRelevancy) {
    const grid = document.querySelector('.products-grid');
    if (grid) {
      const cards = Array.from(grid.children);
      // Keep sold-out cards last (badge text contains "Sold out"), shuffle each group
      const inStock = [];
      const outStock = [];
      cards.forEach((c) => {
        const badge = c.querySelector('.product-card__badge');
        const isSold = badge && /sold/i.test(badge.textContent);
        (isSold ? outStock : inStock).push(c);
      });
      const shuffle = (arr) => {
        for (let i = arr.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
      };
      shuffle(inStock); shuffle(outStock);
      grid.replaceChildren(...inStock, ...outStock);
    }
  }
};

/* --------------------------------------------------------------------------
   Collabs: society pills filter the grid (snippets/collab-pills.liquid)
-------------------------------------------------------------------------- */
function initCollabPills() {
  const bar = document.querySelector('[data-collab-pills]');
  if (!bar) return;
  const pills = [...bar.querySelectorAll('[data-collab-pill]')];
  const cards = [...document.querySelectorAll('.products-grid .product-card')];
  const counts = [...document.querySelectorAll('[data-card-count]')];
  const countText = counts.map((el) => el.textContent);

  const apply = (id, updateUrl) => {
    let shown = 0;
    cards.forEach((card) => {
      const hide = id && card.dataset.collab !== id;
      card.classList.toggle('is-collab-hidden', !!hide);
      if (!hide) shown += 1;
    });
    pills.forEach((p) => {
      const on = p.dataset.collabPill === id;
      p.classList.toggle('is-active', on);
      p.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    counts.forEach((el, i) => {
      el.textContent = id
        ? countText[i].replace(/\d+/, shown).replace(/(products?)\b/i, (w) => (shown === 1 ? w.replace(/s$/i, '') : w.replace(/s?$/i, w.endsWith('S') ? 'S' : 's')))
        : countText[i];
    });
    if (updateUrl) {
      const url = new URL(window.location.href);
      if (id) url.searchParams.set('collab', id); else url.searchParams.delete('collab');
      history.replaceState(history.state, '', url);
    }
  };

  pills.forEach((p) => p.addEventListener('click', () => {
    apply(p.classList.contains('is-active') ? '' : p.dataset.collabPill, true);
  }));
  const start = new URL(window.location.href).searchParams.get('collab');
  if (start && pills.some((p) => p.dataset.collabPill === start)) apply(start, false);
}

/* --------------------------------------------------------------------------
   Init
-------------------------------------------------------------------------- */
const ready = (fn) => {
  if (document.readyState !== 'loading') fn();
  else document.addEventListener('DOMContentLoaded', fn);
};

ready(() => {
  initScrollFadeIn();
  initHeaderScroll();
  initMenuDrawer();
  initSearchDrawer();
  initCartDrawer();
  initBag();
  initMobileBuy();
  initQuickAdd();
  initProductForm();
  initProductGallery();
  initCountdown();
  initDeliveryEstimate();
  initWishlist();
  initAnnouncement();
  initCollectionFilters();
  initCollabPills();
});

// expose for inline use
/* Live countdown on product cards (collab pre-orders): [data-card-countdown="ISO end"], shown as "9d 7h 32m" */
const tickCardCountdowns = () => {
  document.querySelectorAll('[data-card-countdown]').forEach(el => {
    const end = Date.parse(el.dataset.cardCountdown);
    if (isNaN(end)) return;
    let s = Math.floor((end - Date.now()) / 1000);
    if (s <= 0) { el.hidden = true; return; }
    const [ud, uh, um] = (el.dataset.units || 'd,h,m').split(',');
    const d = Math.floor(s / 86400); s -= d * 86400;
    const h = Math.floor(s / 3600); s -= h * 3600;
    const m = Math.floor(s / 60);
    el.textContent = (d > 0 ? `${d}${ud} ` : '') + `${h}${uh} ${m}${um}`;
  });
};
tickCardCountdowns();
document.addEventListener('DOMContentLoaded', tickCardCountdowns);
setInterval(tickCardCountdowns, 30000);

window.SAQR = { openCartDrawer, refreshCartDrawer, addToCart, showToast };


/* Product rails: arrows scroll one screen of cards; disabled at either end */
(function () {
  document.querySelectorAll('[data-rail]').forEach(function (rail) {
    var wrap = rail.closest('.section') || document;
    var prev = wrap.querySelector('[data-rail-prev]'), next = wrap.querySelector('[data-rail-next]');
    if (!prev || !next) return;
    var rtl = getComputedStyle(rail).direction === 'rtl';
    var step = function () { return rail.clientWidth * 0.98; };
    var sync = function () {
      var max = rail.scrollWidth - rail.clientWidth - 2, x = Math.abs(rail.scrollLeft);
      prev.disabled = x <= 2; next.disabled = x >= max;
      wrap.querySelector('[data-rail-arrows]').hidden = max <= 0;
    };
    // own easing: native smooth scroll is ignored in some browsers while scroll-snap is on
    var glide = function (dx) {
      var from = rail.scrollLeft, to = from + dx, t0 = performance.now(), dur = 420;
      rail.style.scrollSnapType = 'none';
      (function tick(now) {
        var k = Math.min(1, (now - t0) / dur), e = 1 - Math.pow(1 - k, 3);
        rail.scrollLeft = from + (to - from) * e;
        if (k < 1) requestAnimationFrame(tick); else { rail.style.scrollSnapType = ''; sync(); }
      })(t0);
    };
    prev.addEventListener('click', function () { glide((rtl ? 1 : -1) * step()); });
    next.addEventListener('click', function () { glide((rtl ? -1 : 1) * step()); });
    rail.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  });
})();


/* Sideways rails (home product rows, bag "Add a little extra"): a trackpad gesture that starts with a
   little sideways movement latches onto the rail, so up/down scrolling stopped moving the page.
   Mostly-vertical wheel movement is handed to the page (or the drawer it sits in); sideways stays on the rail. */
(function () {
  const rails = '.products-rail, .bag-extras__rail';
  document.addEventListener('wheel', (e) => {
    const rail = e.target.closest && e.target.closest(rails);
    if (!rail || e.ctrlKey) return;
    if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
    e.preventDefault();
    const k = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? window.innerHeight : 1;
    const box = rail.closest('.cart-drawer__scroll');
    // 'instant': the page has scroll-behavior: smooth, which would turn every wheel tick into a slow animation
    (box || window).scrollBy({ top: e.deltaY * k, behavior: 'instant' });
  }, { passive: false });
})();

/* WhatsApp buttons: Shopify's exit warning ("Are you sure you want to leave SAQR?") stops every link to a host
   outside its own list, wa.me included. It stands aside when the page handles the click itself, so open the chat
   in a new tab here. */
document.addEventListener('click', (e) => {
  const a = e.target.closest && e.target.closest('a[data-wa-button], a[href^="https://wa.me/"], a[href^="https://api.whatsapp.com/"]');
  if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
  e.preventDefault();
  window.open(a.href, '_blank', 'noopener');
});
